import React, { memo, useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ColorButtons from "../../UIkit/ColorButtons";
import classes from "./DepartmentMultiSelect.module.scss";

// 発注部署の複数選択。部署は20超あるため、ドロップダウンの縦スクロールではなく
// 「ダイアログを開いて拠点ごとのパネルから選ぶ」方式にする(一覧性を優先)。
//
// 契約:
//   departments … 部署マスタ [{ id, name, baseId, baseName }]。渡された並び順を維持する。
//                 非同期ロードのため最初は空配列のことがある。
//   value       … null | string[]。null は「すべて」を意味する。配列のときは非空を保証する。
//   onChange(next) … 空配列で呼んではいけない
//                 (空になる操作は null=すべて に変換してから呼ぶ)。
//
// 表示のルール:
//   ・閉じた状態は1行のテキスト固定(代表1件+「+N件」)。複数選んでも高さが
//     変わらずレイアウトシフトしない。全選択内容はホバー(title)で確認できる
//   ・選択と解除はダイアログ内でのみ行う(「決定」で反映、キャンセル/背景クリックで破棄)
//
// 状態変換のルール(このファイル内で完結させる):
//   1. value===null(すべて)の間は「すべて」にチェックが入り、部署は全てチェックなし
//   2. 「すべて」のチェック操作は、どの状態から行っても「すべて」(トグルにしない)
//   3. null の状態で部署をチェックすると [そのid](「すべて」は自動的に外れる)
//   4. チェックを外していき空になったら null(すべて)に戻す

// 閉じた状態の要約テキスト。複数選択時は代表1件+「+N件」に畳んで高さを固定する。
// 代表はマスタで解決できた最初の1件(マスタ未ロード・不明IDだけなら件数のみ)。
export const summarizeDepartmentSelection = (value, departments) => {
  if (value === null) return "すべて";
  const first = value
    .map((id) => departments.find((department) => department.id === id))
    .find(Boolean);
  if (!first) return `${value.length}件選択`;
  const label = `${first.baseName} ${first.name}`;
  return value.length > 1 ? `${label} +${value.length - 1}件` : label;
};

// パネル内のチェック操作。空配列という状態を作らない(空になったら null=すべて)。
export const toggleDepartmentId = (draft, id) => {
  if (draft === null) return [id];
  const next = draft.includes(id)
    ? draft.filter((selectedId) => selectedId !== id)
    : [...draft, id];
  return next.length > 0 ? next : null;
};

// 拠点チェックの操作: 子部署が全選択なら全解除、そうでなければ全選択にそろえる。
// 他拠点の選択は保持し、解除の結果が空になったら null(すべて)へ。
export const toggleBaseDepartments = (draft, departmentIds) => {
  const current = draft === null ? [] : draft;
  const idSet = new Set(departmentIds);
  const allSelected =
    departmentIds.length > 0 &&
    departmentIds.every((id) => current.includes(id));
  const next = allSelected
    ? current.filter((id) => !idSet.has(id))
    : [...new Set([...current, ...departmentIds])];
  return next.length > 0 ? next : null;
};

// 拠点ごとのグループに分ける(渡された並び順のまま、拠点の出現順を維持)
const groupByBase = (departments) => {
  const groups = [];
  const indexByBaseId = new Map();
  departments.forEach((department) => {
    if (!indexByBaseId.has(department.baseId)) {
      indexByBaseId.set(department.baseId, groups.length);
      groups.push({
        baseId: department.baseId,
        baseName: department.baseName,
        departments: [],
      });
    }
    groups[indexByBaseId.get(department.baseId)].departments.push(department);
  });
  return groups;
};

const DepartmentMultiSelect = memo((props) => {
  const {
    departments = [],
    value,
    onChange,
    disabled = false,
    label = "発注部署",
    // 表示は代表1件+N件に畳むため、幅は控えめでよい(広げすぎると1行が間延びする)
    width = 176,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  // ダイアログ内の編集中の値。「決定」で初めて onChange する
  // (チェックのたびに絞り込むと、選び終わる前の中途半端な結果が背後で点滅するため)
  const [draft, setDraft] = useState(null);
  const [keyword, setKeyword] = useState("");

  const summary = useMemo(
    () => summarizeDepartmentSelection(value, departments),
    [value, departments]
  );
  // 表示は代表1件+N件に畳むため、ホバーで全選択内容(1行=1部署)を確認できるようにする。
  // 「すべて」のときは欄の表示だけで足りるため空にする(空文字なら Tooltip は出ない)
  const summaryTitle = useMemo(() => {
    if (value === null) return "";
    return value
      .map((id) => {
        const department = departments.find((item) => item.id === id);
        return department ? `${department.baseName} ${department.name}` : id;
      })
      .join("\n");
  }, [value, departments]);

  const openDialog = () => {
    if (disabled) return;
    setDraft(value);
    setKeyword("");
    setIsOpen(true);
  };
  const closeDialog = () => setIsOpen(false);
  const applyDraft = () => {
    onChange(draft);
    setIsOpen(false);
  };

  // 部署が多いときのための絞り込み(拠点名+部署名に部分一致)。
  // 絞り込みで見えていない部署のチェック状態は draft にそのまま残る
  const visibleGroups = useMemo(() => {
    const trimmed = keyword.trim();
    const filtered = trimmed
      ? departments.filter((department) =>
          `${department.baseName} ${department.name}`.includes(trimmed)
        )
      : departments;
    return groupByBase(filtered);
  }, [departments, keyword]);

  const isAll = draft === null;

  return (
    <>
      <div className={classes.triggerRow}>
        {/* 選択内容の全文はホバーで MUI Tooltip 表示する
            (ブラウザ標準の title 属性は表示まで約1秒かかり見た目も揃わないため不使用)。
            disabled 中もホバーが届くよう span で包む(MUI公式パターン) */}
        <Tooltip
          title={summaryTitle}
          arrow
          enterDelay={150}
          componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
        >
        <span>
        <TextField
          variant="standard"
          label={label}
          value={summary}
          disabled={disabled}
          onClick={openDialog}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openDialog();
            }
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-haspopup": "dialog" }}
          sx={{
            width,
            "& .MuiInputBase-input": {
              fontSize: 13,
              cursor: disabled ? "default" : "pointer",
              textOverflow: "ellipsis",
            },
          }}
        />
        </span>
        </Tooltip>
        {/* ワンクリックで「すべて」に戻す(ダイアログを開くまでもない解除用)。
            disabled 中は Tooltip が届くよう span で包む(MUI公式パターン) */}
        <Tooltip title="すべての部署にする">
          <span>
            <IconButton
              size="small"
              aria-label="部署の選択をクリア(すべてにする)"
              disabled={disabled || value === null}
              onClick={() => onChange(null)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <Dialog
        open={isOpen}
        onClose={closeDialog}
        // 拠点あたりの部署が少ない(ほぼ1つ)ため、幅は控えめに固定する。
        // 高さも固定にする: 絞り込みで結果が減るたびにダイアログが縮むと
        // ボタンやチェックの位置が動いてしまう(レイアウトシフト)ため
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(720px, calc(100vw - 32px))",
            height: "min(600px, calc(100vh - 64px))",
          },
        }}
      >
        <DialogTitle className={classes.dialogTitle}>発注部署を選択</DialogTitle>
        <DialogContent dividers>
          <div className={classes.toolbar}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isAll}
                  // 「すべて」はトグルではなく、常に「すべて」を指示する
                  onChange={() => setDraft(null)}
                />
              }
              label={<span className={classes.allLabel}>すべて</span>}
            />
            <TextField
              variant="standard"
              placeholder="拠点名・部署名で絞り込み"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 240, "& .MuiInputBase-input": { fontSize: 13 } }}
            />
          </div>
          {visibleGroups.length === 0 && (
            <div className={classes.empty}>
              {departments.length === 0
                ? "部署マスタを読み込んでいます…"
                : "該当する部署がありません"}
            </div>
          )}
          {/* 拠点あたりの部署はほぼ1つのため、縦積みだと間延びする。
              CSS の段組(column)で自動的に複数列へ流し込み、縦を詰める */}
          <div className={classes.panelColumns}>
            {visibleGroups.map((group) => {
              // 拠点チェックは「いま見えている子部署」に対して働く
              // (絞り込み中は絞り込み結果の子だけを全選択/全解除する)
              const groupIds = group.departments.map(
                (department) => department.id
              );
              const selectedCount = isAll
                ? 0
                : groupIds.filter((id) => draft.includes(id)).length;
              const baseChecked =
                groupIds.length > 0 && selectedCount === groupIds.length;
              return (
              <section key={group.baseId} className={classes.baseSection}>
                <div className={classes.baseName}>
                  <FormControlLabel
                    className={classes.baseItem}
                    control={
                      <Checkbox
                        size="small"
                        checked={baseChecked}
                        // 一部だけ選択中は中間状態で表示する
                        indeterminate={selectedCount > 0 && !baseChecked}
                        onChange={() =>
                          setDraft((prev) =>
                            toggleBaseDepartments(prev, groupIds)
                          )
                        }
                      />
                    }
                    label={
                      <span className={classes.baseLabel}>
                        {group.baseName}
                      </span>
                    }
                  />
                </div>
                <div className={classes.departmentGrid}>
                  {group.departments.map((department) => (
                    <FormControlLabel
                      key={department.id}
                      className={classes.departmentItem}
                      control={
                        <Checkbox
                          size="small"
                          checked={!isAll && draft.includes(department.id)}
                          onChange={() =>
                            setDraft((prev) =>
                              toggleDepartmentId(prev, department.id)
                            )
                          }
                        />
                      }
                      label={
                        <span className={classes.departmentLabel}>
                          {department.name}
                        </span>
                      }
                    />
                  ))}
                </div>
              </section>
              );
            })}
          </div>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <span className={classes.draftSummary}>
            {summarizeDepartmentSelection(draft, departments)}
          </span>
          <ColorButtons
            label="キャンセル"
            color="grey"
            onClick={closeDialog}
            width={96}
            fontSize={14}
            bottom={0}
          />
          <ColorButtons
            label="決定"
            color="placed"
            onClick={applyDraft}
            width={96}
            fontSize={14}
            bottom={0}
          />
        </DialogActions>
      </Dialog>
    </>
  );
});

export default DepartmentMultiSelect;
